import type { DeliverableStatus } from "./deliverable"
import type { SchedulingProvider } from "./scheduling-event-type"

export const INTAKE_FORM_LAYOUT_KEYS = ["signature", "contemporary", "traditional", "conversational", "simple"] as const

export type IntakeFormLayoutKey = (typeof INTAKE_FORM_LAYOUT_KEYS)[number]
export type IntakeFormLayoutFlow = "steps" | "document" | "focus"
export type IntakeFormLayoutGeneration = "deterministic" | "agent"
export type IntakeFormStatus = "drafting_schema" | "draft" | "archived"
export type IntakeFormGroupKey = "published" | "draft" | "drafting_schema"
export type IntakeFormInvitationState = "open" | "submitted" | "revoked" | "expired"
export type IntakeFormSubmissionSource = "invitation" | "web"
export type IntakeFormDocumentStatus = "pending" | "uploading" | "completed" | "failed" | "skipped"
export type IntakeFormApplicationTargetAction =
  | "direct_target"
  | "matched_existing"
  | "created_client"
  | "created_prospect"

export interface IntakeFormLayout {
  key: IntakeFormLayoutKey
  label: string
  description: string
  flow: IntakeFormLayoutFlow
  generation: IntakeFormLayoutGeneration
}

export interface IntakeFormFieldOption {
  label: string
  value: string | number | boolean
}

export interface IntakeFormFieldOptionSource {
  repeater?: string
  fields: string[]
}

export type IntakeFormAddressPart = "street1" | "street2" | "city" | "state" | "zip" | "country"

export type IntakeFormHouseholdMemberRole = "dependent_child" | "non_dependent_child" | "other_dependent"

export interface IntakeFormHouseholdMemberMapping {
  role: IntakeFormHouseholdMemberRole
  fields: Partial<
    Record<"first_name" | "last_name" | "full_name" | "date_of_birth" | "relationship" | "is_dependent", string>
  >
}

export type IntakeFormCalculation =
  | { op: "literal"; value: number }
  | { op: "field"; field: string }
  | { op: "sum"; field: string; repeater?: string }
  | {
      op: "add" | "subtract" | "multiply" | "divide"
      left: IntakeFormCalculation
      right: IntakeFormCalculation
    }
  | { op: "age"; field: string }

export type IntakeFormFieldType =
  | "text"
  | "email"
  | "tel"
  | "ssn"
  | "url"
  | "textarea"
  | "number"
  | "currency"
  | "percent"
  | "date"
  | "select"
  | "radio"
  | "boolean"
  | "multi_select"
  | "rating"
  | "address"
  | "acknowledgment"
  | "scheduling"
  | "calendly"
  | "oncehub"
  | "zoom_scheduler"
  | "file"
  | "repeater"

export const SCHEDULING_FIELD_TYPES = ["scheduling", "calendly", "oncehub", "zoom_scheduler"] as const

export function isSchedulingFieldType(fieldType: IntakeFormFieldType): boolean {
  return SCHEDULING_FIELD_TYPES.some((candidate) => candidate === fieldType)
}

export interface IntakeFormField {
  key: string
  type: IntakeFormFieldType
  label: string
  description?: string
  required?: boolean
  placeholder?: string
  step?: string
  // Visibility rules are evaluated server-side (see IntakeFormSubmission#visible_field_keys);
  // the client only carries them through as opaque schema data.
  visible_when?: unknown
  pattern?: string
  mask?: string
  obfuscate?: "last4"
  min_length?: number
  max_length?: number
  rows?: number
  min?: number | string
  max?: number | string
  step_size?: number
  currency?: "USD" | "AUD" | "CAD" | "EUR" | "GBP"
  decimals?: number
  calculation?: IntakeFormCalculation
  display?: boolean
  presentation?: {
    kind: "scale"
    min: number
    max: number
    bands: { max: number; label: string }[]
  }
  options?: IntakeFormFieldOption[]
  options_from?: IntakeFormFieldOptionSource[]
  section?: {
    key: string
    title: string
    description?: string
    visible_when?: unknown
  }
  scale?: 5 | 7 | 10
  parts?: Partial<Record<IntakeFormAddressPart, boolean>>
  default_country?: string
  body?: string
  checkbox_label?: string
  require_typed_name?: boolean
  document_url?: string
  document_version?: string
  accept?: string
  max_size_mb?: number
  multiple?: boolean
  max_files?: number
  event_type_id?: string
  event_type_name?: string
  scheduling_event_type_id?: string
  scheduling_event_type_name?: string
  duration_minutes?: number
  min_days_ahead?: number
  max_days_ahead?: number
  start_time_interval_minutes?: 15 | 30 | 60
  first_name_field?: string
  last_name_field?: string
  full_name_field?: string
  email_field?: string
  other_option?: { value: string | number | boolean; field: string }
  item_config?: {
    fields: IntakeFormField[]
    min_rows?: number
    max_rows?: number
    item_label?: string
  }
  household_member_mapping?: IntakeFormHouseholdMemberMapping
}

export interface IntakeFormStep {
  key: string
  title: string
  description?: string
  visible_when?: unknown
}

export interface IntakeFormSchema {
  version: number
  key: string
  name: string
  description?: string
  eyebrow?: string
  start_button_label?: string
  submit_button_label?: string
  success_message?: { title: string; body?: string }
  steps: IntakeFormStep[]
  fields: IntakeFormField[]
}

export interface IntakeFormFieldMapping {
  field_key?: string
  target_path: string
  target_label?: string
  target_type?: string
}

export interface IntakeFormTemplate {
  key: string
  name: string
  description: string | null
  category: string
  layout_keys: IntakeFormLayoutKey[]
  scheduling_providers: SchedulingProvider[]
  variables?: IntakeFormTemplateVariable[]
  already_added: boolean
}

export interface IntakeFormTemplateVariable {
  key: string
  label: string
  placeholder: string | null
  required: boolean
}

export interface IntakeFormSummary {
  id: string
  guid: string
  owner_book: {
    id: string
    name: string
  }
  owned_by_current_book: boolean
  name: string
  description: string | null
  status: IntakeFormStatus
  record_type: "client" | "prospect" | "contact" | "business"
  layout_key: IntakeFormLayoutKey | null
  editor_revision: number
  generation_error: string | null
  section_count: number
  field_count: number
  web_published: boolean
  published: boolean
  latest_version_number: number | null
  unpublished_changes: boolean
  invitation_count: number
  submission_count: number
  last_invited_at: string | null
  last_submitted_at: string | null
  has_scheduling: boolean
  scheduling_event_type_id: string | null
  scheduling_event_type_name: string | null
  scheduling_provider: SchedulingProvider | null
  created_at: string
  updated_at: string
}

export interface SharedIntakeForm {
  guid: string
  name: string
  description: string | null
  record_type: "client" | "prospect" | "contact" | "business"
  layout_key: IntakeFormLayoutKey
  section_count: number
  field_count: number
  shared_by: string
  source_company_name: string
  requires_scheduling_event_type: boolean
  scheduling_provider: SchedulingProvider | null
  scheduling_event_type_name: string | null
}

export interface GroupedIntakeFormSummary extends IntakeFormSummary {
  groupKey: IntakeFormGroupKey
  groupKeyTotalCount: number
}

export interface IntakeForm extends IntakeFormSummary {
  can_edit: boolean
  can_edit_in_owner_book: boolean
  can_manage_shares: boolean
  book_intake_forms: BookIntakeForm[]
  editor_chat_thread_id: string | null
  html: string
  schema: IntakeFormSchema | Record<string, never>
  field_mappings: Record<string, IntakeFormFieldMapping | string>
  field_catalog: IntakeFormFieldCatalogEntry[]
  layouts: IntakeFormLayout[]
  opened_invitation_count: number
  completed_invitation_count: number
  needs_review_submission_count: number
  notification_recipient_user_ids: string[]
  default_email_subject: string | null
  default_email_body: string | null
  web_publication: IntakeFormWebPublicationReference | null
  latest_version: IntakeFormVersionReference | null
  restricted_client: IntakeFormClientSummary | null
}

export interface BookIntakeForm {
  id: string
  book_id: string
}

export interface IntakeFormClientSummary {
  id: string
  name: string
  type: "Client" | "Prospect"
}

export interface IntakeFormReference {
  id: string
  name: string
}

export interface IntakeFormVersionNameReference {
  id: string
  name: string
  intake_form_id: string
}

/** The latest published version as embedded in the intake form show payload. */
export interface IntakeFormVersionReference {
  id: string
  number: number
  created_at: string
}

/** A published version as returned by the versions index. */
export interface IntakeFormVersion {
  id: string
  number: number
  superseded_at: string | null
  created_at: string
  current: boolean
  published_by: {
    id: string
    name: string
  }
}

/** The web publication as embedded in the intake form show payload. */
export interface IntakeFormWebPublicationReference {
  id: string
  published_at: string | null
  unpublished_at: string | null
  public_url: string
  active: boolean
}

/** The standalone web publication resource nested under a form. */
export interface IntakeFormWebPublication extends IntakeFormWebPublicationReference {
  created_at: string
  updated_at: string
  submission_count: number
}

export interface IntakeFormInvitationSummary {
  id: string
  sent_at: string | null
  expires_at: string | null
  revoked_at: string | null
  first_viewed_at: string | null
  created_at: string
  updated_at: string
  state: IntakeFormInvitationState
  portal_url: string
  intake_form: IntakeFormReference
  client: IntakeFormClientSummary
  intake_form_submission: {
    id: string
    submitted_at: string | null
    read_at: string | null
    draft: boolean
    applied: boolean
  } | null
}

export interface IntakeFormInvitation extends IntakeFormInvitationSummary {
  reminders_cancelled_at: string | null
  delivery_method: "slant_email" | "personal_email"
  creator_copy_method: "bcc" | "cc" | "none"
  delivery_status: "pending" | "scheduled" | "sent" | "failed" | "undeliverable" | "unsubscribed" | "draft"
  recipient_name: string
  recipient_email: string | null
  created_by: {
    id: string
    name: string
  }
  notifications: IntakeFormInvitationNotification[]
  scheduled_reminders: {
    id: string
    position: number
    scheduled_for: string
  }[]
  automatic_reminders_count: number
}

export interface IntakeFormInvitationNotification {
  id: string
  type: "intake_form_invitation" | "intake_form_reminder"
  delivery_status: "pending" | "scheduled" | "sent" | "failed" | "undeliverable" | "unsubscribed" | "draft"
  sent_at: string
  sent_by_name: string | null
}

export interface IntakeFormSubmissionDocument {
  id: string
  field_key: string
  filename?: string
  content_type?: string
  byte_size?: number
  view_url?: string
  download_url?: string
  drive_status: IntakeFormDocumentStatus
  drive_file_id: string | null
  drive_web_url: string | null
  drive_error: string | null
  drive_uploaded_at: string | null
  created_at: string
}

export interface IntakeFormFieldChange {
  field_key: string
  field_label: string
  target_path: string
  target_label: string
  submitted_value: unknown
  current_value: unknown
  field_type: IntakeFormFieldType | null
  target_field_type: IntakeFormFieldType | null
  changed: boolean
  applicable: boolean
  applied: boolean
  applied_value: unknown
  applied_by_name: string | null
  applied_at: string | null
}

export interface IntakeFormApplicationChange {
  target_path: string
  previous_value: unknown
  applied_value: unknown
  person_id?: string
  person_name?: string
}

export interface IntakeFormApplicationMaritalStatusChange extends IntakeFormApplicationChange {
  source_field_key: string
}

export interface IntakeFormApplicationMemberAction {
  key: string
  label: string
  action: "created" | "updated" | "ignored"
  person_id: string | null
  person_name: string | null
  previous_values: Record<string, unknown> | null
  submitted_values: Record<string, unknown>
}

/** The one-shot receipt for a submission that has been applied. Immutable once created. */
export interface IntakeFormApplication {
  id: string
  target_action: IntakeFormApplicationTargetAction
  applied_at: string
  applied_by_name: string | null
  changes: Record<string, IntakeFormApplicationChange>
  marital_status_changes: IntakeFormApplicationMaritalStatusChange[]
  member_actions: IntakeFormApplicationMemberAction[]
}

export interface IntakeFormFieldCatalogEntry {
  path: string
  label: string
  field_type: IntakeFormFieldType
  group: string
  custom_field?: boolean
  options?: string[]
}

export interface IntakeFormHouseholdMemberRow {
  key: string
  field_key: string
  row_index: number
  label: string
  first_name: string | null
  last_name: string | null
  date_of_birth: string | null
  relationship: string | null
  household_role: "dependent_child" | "non_dependent_child" | "other_dependent"
}

export interface IntakeFormHouseholdMember {
  id: string
  name: string
  first_name: string | null
  last_name: string | null
  date_of_birth: string | null
  household_role: string
  updated_at: string
}

export interface IntakeFormApplicationPerson {
  id: string
  name: string
  household_role: string
  marital_status: string
  default_field_group?: "head_of_household" | "spouse_or_partner"
}

export interface IntakeFormMatchCandidate {
  id: string
  name: string
  type: "Client" | "Prospect"
  matched_email: string
  has_storage_folder: boolean
}

export interface IntakeFormSubmissionSummary {
  id: string
  submitted_at: string | null
  draft: boolean
  read_at: string | null
  dismissed_at: string | null
  created_at: string
  updated_at: string
  applied: boolean
  applied_at: string | null
  source: IntakeFormSubmissionSource
  respondent_name: string
  intake_form_version: IntakeFormVersionNameReference
  client: { id: string; name: string; type: "Client" | "Prospect" } | null
  document_count: number
}

export interface IntakeFormAggregatedResponse extends IntakeFormSubmissionSummary {
  answers: Record<string, unknown>
}

export interface IntakeFormSubmission extends IntakeFormSubmissionSummary {
  sensitive_answer_categories?: Record<string, "ssn" | "drivers_license_number">
  answers: Record<string, unknown>
  answer_schema: IntakeFormSchema
  visible_field_keys: string[]
  has_consent: boolean
  has_consent_record: boolean
  recipient_name: string
  application: IntakeFormApplication | null
  created_as_note?: boolean
  target: { id: string; name: string; type: "Client" | "Prospect"; has_storage_folder: boolean } | null
  match_candidates: IntakeFormMatchCandidate[]
  field_catalog: IntakeFormFieldCatalogEntry[]
  mapped_target_paths: string[]
  planning_deliverable: {
    id: string
    label: string | null
    status: DeliverableStatus
    action_link_id: string | null
  } | null
  household_member_rows: IntakeFormHouseholdMemberRow[]
  application_people: IntakeFormApplicationPerson[]
  household_members: IntakeFormHouseholdMember[]
  navigation: { previous_id: string | null; next_id: string | null }
  details: {
    source: string
    version_digest: string
    elapsed_seconds?: number
    ip_address: string | null
    user_agent: string | null
    answer_count: number
    field_count: number
  }
  intake_form_invitation: {
    id: string
    state: IntakeFormInvitationState
  } | null
  field_changes: IntakeFormFieldChange[]
  documents: IntakeFormSubmissionDocument[]
}

export interface IntakeFormConsentDocumentDelivery {
  id: string
  web_url: string | null
  filename: string
}
